/**
 * A finished experiment, rendered.
 *
 * What the assistant understood, the hypothesis it stated BEFORE running
 * anything, the design, the measured readouts, the findings, the charts, a
 * table of every condition run, and the sharpened question at the end. The
 * order is the inquiry cycle itself.
 *
 * ITS OWN MODULE because there are two places a report is read now. The
 * tool panel has always shown them; a technician can also run an experiment
 * inside a conversation (src/talk/experiments.ts), and it is the same report
 * from the same assistant, so it has to be the same component - a second
 * renderer would drift, and the two would stop agreeing about what an
 * experiment said. Split out of AssistantPane rather than imported from it
 * so the chat does not drag that pane's auth subscription and sign-in gate
 * into the entry chunk to draw a table.
 */

import { useMemo } from 'react'
import type { ExperimentReport } from '../../assistant/types'
import { t, tOr } from '../../i18n/i18n'
import { ChartView } from './ChartView'
import { StatTile } from './primitives'
/**
 * One experiment's report.
 *
 * Exported because the tool's panel is no longer the only place a report is
 * read: a technician can run an experiment inside a conversation now
 * (src/talk/experiments.ts), and it is the same report from the same
 * assistant, so it is rendered by the same component rather than by a second
 * one that would drift.
 */
export function ReportView({
  lead,
  report,
  onAsk,
}: {
  lead: string
  report: ExperimentReport
  onAsk: (question: string) => void
}) {
  const r = report
  const block = (k: string, v: string) => (
    <div className="blk">
      <div className="k">{k}</div>
      <div className="v">{v}</div>
    </div>
  )
  return (
    <div className="turn assistant">
      <div className="lead">{lead}</div>
      {r.hypothesis && block(t('assistant.Hypothesis'), r.hypothesis)}
      {r.design && block(t('assistant.Design'), r.design)}
      {r.readouts.length > 0 && (
        <div className="blk">
          <div className="k">{t('assistant.Measured')}</div>
          <div className="grid-2" style={{ marginTop: 6 }}>
            {r.readouts.map((ro, i) => (
              <StatTile key={i} r={ro} />
            ))}
          </div>
        </div>
      )}
      {r.findings.length > 0 && (
        <div className="blk">
          <div className="k">
            {r.runs.length ? t('assistant.Findings') : t('assistant.CanInvestigate')}
          </div>
          <ul>
            {r.findings.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}
      {r.charts.map((spec, i) => (
        <ReportChart key={i} spec={spec} />
      ))}
      {r.runs.length > 0 && <RunsTable report={r} />}
      {r.suggestion && (
        <div className="btn-row" style={{ margin: '10px 0' }}>
          <button className="btn primary" onClick={() => r.suggestion!.apply()}>
            {r.suggestion.label}
          </button>
        </div>
      )}
      {/* Offered above `descend` because it is the more urgent of the two:
          the student can take the nearest experiment without retyping. */}
      {r.offer && (
        <div className="btn-row" style={{ margin: '10px 0' }}>
          <button className="btn primary" onClick={() => onAsk(r.offer!.question)}>
            {r.offer.label}
          </button>
        </div>
      )}
      {r.descend && (
        <div className="btn-row" style={{ margin: '10px 0' }}>
          <button className="btn" onClick={() => onAsk(r.descend!.prompt)}>
            {t('assistant.Descend', {
              topic: tOr(`assistant.topic.${r.descend.topic}`, r.descend.topic),
            })}
          </button>
        </div>
      )}
      {r.followUp && (
        <div className="followup">
          <div className="k">{t('assistant.NextQuestion')}</div>
          <div>{r.followUp}</div>
        </div>
      )}
    </div>
  )
}

function ReportChart({ spec }: { spec: ExperimentReport['charts'][number] }) {
  const options = useMemo(
    () => ({
      title: spec.title,
      xLabel: spec.xLabel,
      yLabel: spec.yLabel,
      series: spec.series,
      annotations: spec.annotations,
    }),
    [spec],
  )
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--ink-2)', margin: '12px 0 2px', lineHeight: 1.4 }}>
        {spec.title}
      </div>
      <ChartView width={348} height={176} options={options} />
    </div>
  )
}

/** Every experiment also gets a table, so identity is never colour-alone. */
function RunsTable({ report }: { report: ExperimentReport }) {
  const columns = Object.keys(report.runs[0].values)
  return (
    <div className="blk">
      <div className="k">{t('assistant.ConditionsRun')}</div>
      <table className="table">
        <thead>
          <tr>
            <th>{t('assistant.Condition')}</th>
            {columns.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {report.runs.map((row, i) => (
            <tr key={i}>
              <td>{row.label}</td>
              {columns.map((c) => (
                <td key={c}>{row.values[c] ?? '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
